import { texts } from '@/texts'

/**
 * Calculate life path number from birth date (MMDD or DDMMYYYY format)
 */
export function calculateLifePathNumber(birthDate: string): number {
  const digits = birthDate.replace(/\D/g, '').split('').map(Number)
  return reduceToSingleDigit(digits.reduce((a, b) => a + b, 0))
}

// German umlauts and ss are normalized before letter-based calculations.
function normalizeName(name: string): string {
  return name
    .replace(/\u00DF/g, 'ss')
    .toUpperCase()
    .replace(/\u00C4/g, 'A')
    .replace(/\u00D6/g, 'O')
    .replace(/\u00DC/g, 'U')
}

const BASE_VOWELS = 'AEIOU'

/**
 * Splits a full name into vowel- and consonant-letters, applying the rule for "Y":
 * Y counts as a VOWEL when it is the first vowel within its name part \u2014 first
 * name and last name (and hyphenated parts) are evaluated independently \u2014 e.g.
 * Yvonne, Yves, Lydia. As soon as a vowel already precedes it in the same part,
 * Y counts as a CONSONANT \u2014 e.g. Evelyne, Melchyor.
 */
export function splitVowelsConsonants(name: string): { vowels: string[]; consonants: string[] } {
  const vowels: string[] = []
  const consonants: string[] = []

  for (const part of name.split(/[\s-]+/)) {
    const letters = normalizeName(part).split('').filter(char => /[A-Z]/.test(char))
    let seenVowel = false
    for (const char of letters) {
      if (char === 'Y') {
        if (seenVowel) {
          consonants.push(char)
        } else {
          vowels.push(char)
          seenVowel = true
        }
      } else if (BASE_VOWELS.includes(char)) {
        vowels.push(char)
        seenVowel = true
      } else {
        consonants.push(char)
      }
    }
  }

  return { vowels, consonants }
}

/**
 * Calculate destiny number from the consonants of the full name (first + last).
 * Y is classified contextually (see splitVowelsConsonants).
 */
export function calculateDestinyNumber(name: string): number {
  const sum = splitVowelsConsonants(name).consonants
    .reduce((acc, char) => acc + getLetterValue(char), 0)

  return reduceToSingleDigit(sum)
}

/**
 * Calculate soul urge number from the vowels of the full name.
 * Y is classified contextually (see splitVowelsConsonants).
 */
export function calculateSoulUrgeNumber(name: string): number {
  const sum = splitVowelsConsonants(name).vowels
    .reduce((acc, char) => acc + getLetterValue(char), 0)

  return reduceToSingleDigit(sum)
}

/**
 * Calculate expression number (all letters in name)
 */
export function calculateExpressionNumber(name: string): number {
  const sum = normalizeName(name)
    .split('')
    .filter(char => /[A-Z]/.test(char))
    .reduce((acc, char) => acc + getLetterValue(char), 0)

  return reduceToSingleDigit(sum)
}

/**
 * Calculate birthday number (just the day of birth reduced to single digit)
 */
export function calculateBirthdayNumber(dateString: string): number {
  const dateObj = new Date(dateString)
  const day = dateObj.getDate()
  return reduceToSingleDigit(day)
}

/**
 * Calculate personal year number for a given year
 */
export function calculatePersonalYearNumber(birthMonth: number, birthDay: number, year: number): number {
  const digits = `${birthMonth}${birthDay}${year}`
    .split('')
    .map(Number)

  return reduceToSingleDigit(digits.reduce((a, b) => a + b, 0))
}

function getIsoWeekNumber(date: Date): number {
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const day = utcDate.getUTCDay() || 7
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - day)
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1))
  return Math.ceil((((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
}

export function calculateUniversalWeekNumber(date: Date): number {
  return reduceToSingleDigit(getIsoWeekNumber(date))
}

export function calculatePersonalWeekNumber(
  birthMonth: number,
  birthDay: number,
  date: Date,
): number {
  const personalYear = calculatePersonalYearNumber(birthMonth, birthDay, date.getFullYear())
  const universalWeek = calculateUniversalWeekNumber(date)
  return reduceToSingleDigit(personalYear + universalWeek)
}

/**
 * Persönlicher Tag = Quersumme aller Ziffern von
 * Geburtstag + Geburtsmonat + aktueller Tag + Monat + Jahr
 * reduziert auf die Endziffer (1–9).
 *
 * Geburtsjahr fließt bewusst NICHT ein — entspricht der
 * verbreiteten "Numeroskop"-Tagesrechnung.
 * Beispiel: Geb. 5.10.1980, Heute 21.4.2026
 *   → 5+1+0+2+1+4+2+0+2+6 = 23 → 5
 */
export function calculatePersonalDayNumber(
  birthMonth: number,
  birthDay: number,
  year: number,
  month: number,
  day: number,
): number {
  const digits = `${birthDay}${birthMonth}${day}${month}${year}`.split('').map(Number)
  return reduceToSingleDigit(digits.reduce((a, b) => a + b, 0))
}

/**
 * Universeller Tag = Quersumme aller Ziffern des aktuellen Datums,
 * reduziert auf die Endziffer. Für alle Menschen gleich.
 * Beispiel: 21.4.2026 → 2+1+4+2+0+2+6 = 17 → 8
 */
export function calculateUniversalDayNumber(
  year: number,
  month: number,
  day: number,
): number {
  const digits = `${day}${month}${year}`.split('').map(Number)
  return reduceToSingleDigit(digits.reduce((a, b) => a + b, 0))
}

export interface NumerologyMeaning {
  meaning: string
  characteristics: string[]
  colors: string[]
  description?: string
  strengths?: string[]
  challenges?: string[]
  spiritual?: string
}

type RawMeaning = (typeof texts.numerology.meanings)[1]

/**
 * Get numerological meaning for a number (1-9). Texts come from `src/texts.ts`.
 * Returns a mutable copy so callers can't accidentally mutate the readonly source.
 */
export function getNumerologyMeaning(number: number): NumerologyMeaning {
  const all = texts.numerology.meanings as unknown as Record<number, RawMeaning>
  const found = all[number] ?? all[1]
  return {
    meaning: found.meaning,
    characteristics: [...found.characteristics],
    colors: [...found.colors],
    description: found.description,
    strengths: [...found.strengths],
    challenges: [...found.challenges],
    spiritual: found.spiritual,
  }
}

function getLetterValue(letter: string): number {
  const values: Record<string, number> = {
    A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, I: 9,
    J: 1, K: 2, L: 3, M: 4, N: 5, O: 6, P: 7, Q: 8, R: 9,
    S: 1, T: 2, U: 3, V: 4, W: 5, X: 6, Y: 7, Z: 8,
  }
  return values[letter] || 0
}

function reduceToSingleDigit(num: number): number {
  while (num >= 10) {
    num = num.toString().split('').reduce((a, b) => a + parseInt(b), 0)
  }
  return num
}

const MASTER_NUMBERS = [11, 22, 33, 44] as const

export function detectMasterNumber(birthDate: string): number | null {
  const digits = birthDate.replace(/\D/g, '').split('').map(Number)
  let sum = digits.reduce((a, b) => a + b, 0)
  while (sum >= 10) {
    if ((MASTER_NUMBERS as readonly number[]).includes(sum)) return sum
    sum = sum.toString().split('').reduce((a, b) => a + parseInt(b), 0)
  }
  return null
}
